/*
 * Copyright (c) 2026. Bluemate B.V.
 */

import {ErrorType, ResponseData} from '@dvrd/dvr-controls';

import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {useSearchParams} from 'react-router';

import {AppContext} from '../../misc/appContext';

import {normalizeLanguage} from '../../../i18n/config';

import {OAuthAuthorizeData, OAuthAuthorizeErrorType} from '../../../util/interfaces';

import {authorizeOAuth, getUser, logout} from '../../../util/requestSender';

import {setCSRF} from '../../../util/storageUtil';

import OAuthAuthorize, {ConnectView} from './oauthAuthorize';

// Long enough for the "connected" tick to register before the browser leaves for
// the client's callback, short enough not to feel like a stall.
const SUCCESS_REDIRECT_DELAY = 800;

function readAuthorizeParams(searchParams: URLSearchParams): OAuthAuthorizeData | null {
    const clientId = searchParams.get('client_id') ?? '';
    const redirectUri = searchParams.get('redirect_uri') ?? '';
    const codeChallenge = searchParams.get('code_challenge') ?? '';
    if (!clientId || !redirectUri || !codeChallenge) return null;
    return {
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: searchParams.get('response_type') ?? '',
        scope: searchParams.get('scope') ?? '',
        state: searchParams.get('state'),
        code_challenge: codeChallenge,
        code_challenge_method: searchParams.get('code_challenge_method') ?? ''
    };
}

function accountLabel(user: Record<string, any> | undefined): string {
    if (!user) return '';
    const name = [user.firstname, user.infix, user.lastname].filter(Boolean).join(' ');
    return name || user.email || '';
}

// Best-effort return without a validated redirect: close the popup the AI app
// opened; in a same-tab flow walk the history back to the app.
function closeOrBack() {
    window.close();
    window.setTimeout(() => {
        if (history.length > 1) history.back();
        else window.location.assign('https://claude.ai');
    }, 150);
}

export default function OAuthAuthorizeController() {
    const {i18n} = useTranslation();
    const context = useContext(AppContext);
    const [searchParams] = useSearchParams();
    const [view, setView] = useState<ConnectView>('resolving');
    const [account, setAccount] = useState<string | null>(null);
    const [error, setError] = useState<ErrorType>(null);
    const [canRetry, setCanRetry] = useState(false);
    const authorizeParams = useMemo(() => readAuthorizeParams(searchParams), [searchParams]);
    const redirectUrl = useRef<string | null>(null);
    const redirectTimeout = useRef<number | null>(null);

    // A 401/403 from /v1/user is the normal logged-out case (the route is
    // public, there is no guard in front of it) — only other failures are real.
    function noSessionOr(status: number | undefined, fallback: VoidFunction) {
        if (status === 401 || status === 403) {
            setCSRF('');
            setView('signIn');
            return;
        }
        fallback();
    }

    function startAuthorization() {
        if (!authorizeParams) return onDeadEnd('invalidRequest', false);
        setView('resolving');
        setError(null);
        getUser({
            callback: (response: ResponseData) => {
                const token = response.success ? (response.csrf_token ?? '') : '';
                setCSRF(token);
                if (!token) return setView('signIn');
                if (response.oauth_fresh !== true) return setView('signIn');
                setAccount(accountLabel(response.user));
                setView('chooser');
            },
            errorCallback: (response: Response) => noSessionOr(response?.status, () => onDeadEnd(null, true))
        });
    }

    function onContinue() {
        requestAuthorization();
    }

    // The CSRF token is bound to the session's authentication time, so the one
    // fetched before the sign-in is stale — re-read the user to get the fresh
    // token before authorizing.
    function onSignedIn() {
        setView('authorizing');
        getUser({
            callback: (response: ResponseData) => {
                const token = response.success ? (response.csrf_token ?? '') : '';
                setCSRF(token);
                if (!token) return onDeadEnd(null, true);
                requestAuthorization();
            },
            errorCallback: (response: Response) => noSessionOr(response?.status, () => onDeadEnd(null, true))
        });
    }

    function requestAuthorization() {
        if (!authorizeParams) return;
        setView('authorizing');
        authorizeOAuth({
            data: authorizeParams,
            callback: onAuthorizeResponse,
            errorCallback: () => onDeadEnd(null, true)
        });
    }

    function onAuthorizeResponse(data: ResponseData) {
        if (data.success && data.redirect_url) return onAuthorized(data.redirect_url);
        if (data.type === OAuthAuthorizeErrorType.REAUTH_REQUIRED) return setView('signIn');
        // Every other refusal ends the flow: hand the browser back to the AI
        // app with a validated access_denied redirect instead of a dead card.
        returnDenied();
    }

    function onAuthorized(url: string) {
        redirectUrl.current = url;
        setView('done');
        redirectTimeout.current = window.setTimeout(() => window.location.assign(url), SUCCESS_REDIRECT_DELAY);
    }

    function onSwitchAccount() {
        setView('resolving');
        logout({
            callback: () => {
                context.onChangeContext({accounts: [], account: null, user: null});
                setAccount(null);
                setView('signIn');
            },
            errorCallback: () => {
                context.onChangeContext({accounts: [], account: null, user: null});
                setAccount(null);
                setView('signIn');
            }
        });
    }

    function onCancel() {
        returnDenied();
    }

    function returnDenied() {
        if (!authorizeParams) return closeOrBack();
        setView('cancelling');
        authorizeOAuth({
            data: {...authorizeParams, action: 'deny'},
            callback: (response: ResponseData) => {
                if (response.success && response.redirect_url) return window.location.assign(response.redirect_url);
                onDeadEnd(null, false);
            },
            errorCallback: () => onDeadEnd(null, false)
        });
    }

    // The only surviving error card: the redirect target cannot be validated
    // (broken request) or the network is down. Everything else returns to the
    // AI app by redirect. The state holds a message key, not a translated
    // string, so the card follows later language switches.
    function onDeadEnd(reason: 'invalidRequest' | null, retryable: boolean) {
        setError(reason ?? 'failed');
        setCanRetry(retryable);
        setView('error');
    }

    function onRetry() {
        startAuthorization();
    }

    // Returning from the error card is still a refusal: hand the browser back
    // through the validated deny redirect whenever the request allows it, so
    // the user lands in the AI app instead of one history step earlier.
    function onReturn() {
        if (redirectUrl.current) return window.location.assign(redirectUrl.current);
        if (!authorizeParams) return closeOrBack();
        authorizeOAuth({
            data: {...authorizeParams, action: 'deny'},
            callback: (response: ResponseData) => {
                if (response.success && response.redirect_url) return window.location.assign(response.redirect_url);
                closeOrBack();
            },
            errorCallback: () => closeOrBack()
        });
    }

    // The MCP consent page forwards the language the user picked there as
    // ui_locales, so step 2 opens in the same language instead of flipping.
    function applyRequestedLanguage() {
        const requested = (searchParams.get('ui_locales') ?? '').trim().split(/\s+/)[0]?.toLowerCase() ?? '';
        if (!requested.startsWith('nl') && !requested.startsWith('en')) return;
        void i18n.changeLanguage(normalizeLanguage(requested));
    }

    useEffect(() => {
        applyRequestedLanguage();
        startAuthorization();
        return () => {
            if (redirectTimeout.current) window.clearTimeout(redirectTimeout.current);
        };
    }, []);

    return (
        <OAuthAuthorize
            view={view}
            account={account}
            error={error}
            canRetry={canRetry}
            onContinue={onContinue}
            onSwitchAccount={onSwitchAccount}
            onCancel={onCancel}
            onRetry={onRetry}
            onReturn={onReturn}
            onSignedIn={onSignedIn}
        />
    );
}
